export interface ISSEFrame {
  id: string | null;
  event: string | null;
  data: string;
}

const FIELD_SEPARATOR = ':';

/**
 * Um chunk de rede pode acabar no meio de um caractere multibyte, de um CRLF ou
 * de um JSON. Só devolve frames fechados por linha em branco; o resto fica no
 * buffer esperando o próximo chunk.
 */
export class SseParser {
  private readonly decoder = new TextDecoder('utf-8');
  private buffer = '';
  private lines: string[] = [];

  push(chunk: Uint8Array): ISSEFrame[] {
    this.buffer += this.decoder.decode(chunk, { stream: true });
    return this.drain();
  }

  /**
   * Frame sem linha em branco no fim do stream é descartado. O decode final
   * solta os bytes multibyte retidos, que voltariam como U+FFFD se o mesmo
   * parser fosse reusado numa reconexão.
   */
  finish(): void {
    this.decoder.decode();
    this.buffer = '';
    this.lines = [];
  }

  private drain(): ISSEFrame[] {
    const frames: ISSEFrame[] = [];
    let newline = this.buffer.indexOf('\n');

    while (newline !== -1) {
      const raw = this.buffer.slice(0, newline);
      this.buffer = this.buffer.slice(newline + 1);

      const line = raw.endsWith('\r') ? raw.slice(0, -1) : raw;
      if (line === '') {
        const frame = this.buildFrame();
        if (frame) {
          frames.push(frame);
        }
        this.lines = [];
      } else {
        this.lines.push(line);
      }

      newline = this.buffer.indexOf('\n');
    }

    return frames;
  }

  private buildFrame(): ISSEFrame | null {
    let id: string | null = null;
    let event: string | null = null;
    const data: string[] = [];

    for (const line of this.lines) {
      if (line.startsWith(FIELD_SEPARATOR)) {
        continue;
      }

      const separator = line.indexOf(FIELD_SEPARATOR);
      const field = separator === -1 ? line : line.slice(0, separator);
      const rawValue = separator === -1 ? '' : line.slice(separator + 1);
      const value = rawValue.startsWith(' ') ? rawValue.slice(1) : rawValue;

      if (field === 'data') {
        data.push(value);
      } else if (field === 'event') {
        event = value;
      } else if (field === 'id') {
        id = value;
      }
    }

    if (!data.length) {
      return null;
    }

    return { id, event, data: data.join('\n') };
  }
}
